import { useEffect, useState } from 'react';
import { ActivityIndicator, ScrollView, Share, StyleSheet, View } from 'react-native';
import { Button, IconButton, Surface, Text, useTheme } from 'react-native-paper';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Stack, useRouter } from 'expo-router';
import { useTranslation } from 'react-i18next';

import { advanceOnboardingState, getOnboardingState } from '@/src/api/apiService';
import { APP_ORIGIN } from '@/src/api/config';
import { clearOwnerDriverStorage } from '@/src/features/owner/ownerDriverPrompt';
import { removeItem } from '@/src/lib/storage';
import {
  isAuthoritativeOnboardingState,
  routeForOnboardingState,
} from '@/src/utils/onboardingResume';

const OWNER_REGISTRATION_URL = `${APP_ORIGIN}/register/owner`;

export default function CompanyContactPendingScreen() {
  const { t } = useTranslation('register');
  const theme = useTheme();
  const router = useRouter();
  const [isWaiting, setIsWaiting] = useState(false);
  const [selfPayTransitioning, setSelfPayTransitioning] = useState(false);
  const [selfPayError, setSelfPayError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    void getOnboardingState()
      .then((response) => {
        if (cancelled) return;
        if (!response.ok || !isAuthoritativeOnboardingState(response.data)) {
          router.replace('/onboarding/state-error');
          return;
        }
        if (
          response.data.persona === 'DRV-CO'
          && response.data.current_step === 'company_contact_submitted'
          && response.data.completed === false
        ) {
          setIsWaiting(true);
          return;
        }
        router.replace(routeForOnboardingState(response.data));
      })
      .catch(() => {
        if (!cancelled) router.replace('/onboarding/state-error');
      });

    return () => {
      cancelled = true;
    };
  }, [router]);

  const shareWithManager = async () => {
    try {
      await Share.share({ message: OWNER_REGISTRATION_URL, url: OWNER_REGISTRATION_URL });
    } catch {
      // The URL remains selectable for manual copy.
    }
  };

  const logout = async () => {
    await Promise.all([
      removeItem('userToken'),
      clearOwnerDriverStorage(),
    ]);
    router.replace('/');
  };

  const switchToSelfPay = async () => {
    if (selfPayTransitioning) return;
    setSelfPayTransitioning(true);
    setSelfPayError(null);
    try {
      const response = await advanceOnboardingState('DRV-SELF', 'product');
      if (
        response.ok
        && isAuthoritativeOnboardingState(response.data)
        && response.data.persona === 'DRV-SELF'
        && routeForOnboardingState(response.data) === '/dashboard/self-pay'
      ) {
        router.replace('/dashboard/self-pay');
        return;
      }
      setSelfPayError(
        t(
          'companyContactPendingSelfPayError',
          'Não foi possível mudar para pagamento próprio. Tente novamente.',
        ),
      );
    } catch {
      setSelfPayError(
        t(
          'companyContactPendingSelfPayError',
          'Não foi possível mudar para pagamento próprio. Tente novamente.',
        ),
      );
    } finally {
      setSelfPayTransitioning(false);
    }
  };

  if (!isWaiting) {
    return (
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <View style={styles.loading}>
          <ActivityIndicator accessibilityLabel={t('loading', 'A carregar')} />
        </View>
      </SafeAreaView>
    );
  }

  return (
    <>
      <Stack.Screen options={{ headerShown: false }} />
      <SafeAreaView style={[styles.container, { backgroundColor: theme.colors.background }]}>
        <ScrollView
        contentContainerStyle={styles.scrollContent}
        showsVerticalScrollIndicator={false}
        testID="company-contact-pending-scroll"
      >
        <Surface style={styles.card} elevation={1} testID="company-contact-pending">
          <IconButton
            icon="logout"
            size={20}
            accessibilityLabel={t('companyContactPendingLogout', 'Sair')}
            onPress={() => void logout()}
            style={styles.logout}
            testID="company-contact-logout"
          />
          <Text
            accessibilityRole="header"
            variant="headlineSmall"
            style={[styles.title, { color: theme.colors.primary }]}
          >
            {t('companyContactPendingTitle', 'Aguardando a empresa')}
          </Text>
          <Text variant="bodyMedium" style={styles.body}>
            {t(
              'companyContactPendingBody',
              'A empresa ainda não ativou o seu acesso. Pode aguardar pela empresa ou pagar a sua própria licença para avançar já.',
            )}
          </Text>

          <View
            style={[styles.selfPayPanel, { backgroundColor: theme.colors.primaryContainer }]}
            testID="company-contact-self-pay-panel"
          >
            <Text
              accessibilityRole="header"
              variant="titleMedium"
              style={[styles.optionTitle, { color: theme.colors.onPrimaryContainer }]}
            >
              {t('companyContactPendingSelfPayTitle', 'Avançar sem esperar')}
            </Text>
            <Text
              variant="bodyMedium"
              style={[styles.optionBody, { color: theme.colors.onPrimaryContainer }]}
            >
              {t(
                'companyContactPendingSelfPayBody',
                'Se escolheu a empresa por engano ou já não quer esperar, mude para pagamento próprio. Só paga depois de escolher e confirmar o plano.',
              )}
            </Text>
            <Button
              mode="contained"
              icon="credit-card-outline"
              buttonColor={theme.colors.tertiary}
              textColor={theme.colors.onTertiary}
              // Dark mode: the brand-blue button is only 1.6:1 against the primaryContainer panel,
              // so outline it with onPrimaryContainer (7.2:1 against the panel) to keep its edge visible.
              style={theme.dark ? { borderWidth: 1, borderColor: theme.colors.onPrimaryContainer } : undefined}
              onPress={() => void switchToSelfPay()}
              loading={selfPayTransitioning}
              disabled={selfPayTransitioning}
              contentStyle={styles.actionContent}
              testID="company-contact-self-pay"
            >
              {t('companyContactPendingSelfPay', 'Pagar a minha própria licença')}
            </Button>
            {selfPayError && (
              <Text
                accessibilityRole="alert"
                style={[styles.selfPayError, { color: theme.colors.error }]}
                testID="company-contact-self-pay-error"
              >
                {selfPayError}
              </Text>
            )}
          </View>

          <View style={styles.waitSection} testID="company-contact-wait-section">
            <Text accessibilityRole="header" variant="titleMedium" style={styles.waitTitle}>
              {t('companyContactPendingWaitTitle', 'Continuar a aguardar')}
            </Text>
            <Text variant="bodyMedium" style={styles.waitBody}>
              {t(
                'companyContactPendingWaitBody',
                'Partilhe este link com o gestor para acelerar a ativação.',
              )}
            </Text>
            <View style={[styles.urlBox, { backgroundColor: theme.colors.surfaceVariant }]}>
              <Text selectable variant="bodySmall" style={styles.url} testID="company-contact-owner-url">
                {OWNER_REGISTRATION_URL}
              </Text>
            </View>
            <Button
              mode="outlined"
              icon="share-variant"
              onPress={() => void shareWithManager()}
              contentStyle={styles.actionContent}
              testID="company-contact-share"
            >
              {t('companyContactPendingShare', 'Partilhar link com o gestor')}
            </Button>
          </View>
        </Surface>
        </ScrollView>
      </SafeAreaView>
    </>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  loading: { alignItems: 'center', flex: 1, justifyContent: 'center' },
  scrollContent: { flexGrow: 1, justifyContent: 'center', paddingHorizontal: 16, paddingVertical: 16 },
  card: { alignSelf: 'center', borderRadius: 16, maxWidth: 560, padding: 16, width: '100%' },
  logout: { position: 'absolute', right: 4, top: 4, zIndex: 1 },
  title: { marginBottom: 6, paddingHorizontal: 36, textAlign: 'center' },
  body: { marginBottom: 16, textAlign: 'center' },
  selfPayPanel: { borderRadius: 12, padding: 14 },
  optionTitle: { marginBottom: 6 },
  optionBody: { marginBottom: 12 },
  actionContent: { minHeight: 48 },
  selfPayError: { marginTop: 8, textAlign: 'center' },
  waitSection: { marginTop: 16 },
  waitTitle: { marginBottom: 6, textAlign: 'center' },
  waitBody: { marginBottom: 8, textAlign: 'center' },
  urlBox: { borderRadius: 8, marginBottom: 8, paddingHorizontal: 12, paddingVertical: 8 },
  url: { textAlign: 'center' },
});